import { 
    gql, 
    useQuery 
} from "@apollo/client";
import { FindAllCategoriesAdminQueryQuery, FindAllCategoriesAdminQueryQueryVariables } from "../../../../../schemaTypes";
import { useCallback } from "react";

export const findAllCategoriesAdminQuery = gql`
  query FindAllCategoriesAdminQuery(
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllCategoriesAdmin(
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        name
        imgSrc
        createdBy {
            id
            role
            admin {
              id
              level
              firstname
              lastname
            }
        }
        createdAt
        updatedAt
        isArchive
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindAllCategoriesAdmin {
  data: FindAllCategoriesAdminQueryQuery["findAllCategoriesAdmin"]["data"]
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreCategories: boolean;
}

interface Props {
  variables: FindAllCategoriesAdminQueryQueryVariables;
  children: (data: WithFindAllCategoriesAdmin) => React.ReactElement | null;
}

export const FindAllCategoriesAdminController: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllCategoriesAdminQueryQuery,
    FindAllCategoriesAdminQueryQueryVariables
  >(findAllCategoriesAdminQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const findAllCategoriesAdminData = data?.findAllCategoriesAdmin?.data || [];
  const page = data?.findAllCategoriesAdmin?.page || variables.page;
  const pageSize = data?.findAllCategoriesAdmin?.pageSize || variables.pageSize;
  const total = data?.findAllCategoriesAdmin?.total || 0;
  const totalPages = data?.findAllCategoriesAdmin?.totalPages || 0;
  const hasMoreCategories = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreCategories) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findAllCategoriesAdmin) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllCategoriesAdmin: {
            ...fetchMoreResult.findAllCategoriesAdmin,
            data: [
              ...(prev?.findAllCategoriesAdmin?.data || []),
              ...(fetchMoreResult.findAllCategoriesAdmin.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreCategories]);

  return children({
    data: findAllCategoriesAdminData as any,
    loading,
    loadMore,
    hasMoreCategories,
    page,
    pageSize,
    total,
    totalPages,
  });
};